import type { ChangeEvent, FC } from "react";
import QuickSearch from "../../components/Search/components/QuickSearch/QuickSearch";
import ImageContainer from "../../components/general/ImageContainers/ImageContainer";
import List from "../../components/general/List";
import ListItem from "../../components/general/List/components/ListItem/ListItem";
import ShowAll from "../../components/general/List/components/ShowAll";
import type { ActiveSearchType } from "../../context/app/reducer";
import type { SearchResultCMS } from "../../hooks/search/types";
import { useTranslatedPaths } from "../../hooks/use-translated-paths";

export type QuickSearchProductProps = {
	uid: string;
	productName: string;
	productImage: { filename: string; alt: string };
	price: string;
	productLink: string;
	variantLabel: string;
};

type QuickSearchComponentProps = {
	isSearchOpen: boolean;
	labels: Record<
		| "getHelpLabel"
		| "customerServiceLabel"
		| "faqLabel"
		| "shippingLabel"
		| "suggestionsLabel"
		| "popularLabel"
		| "productsLabel"
		| "trendingLabel"
		| "pagesLabel"
		| "showAllLabel",
		string
	>;
	pages: SearchResultCMS[];
	products: QuickSearchProductProps[];
	searchList: string[];
	searchValue: string;
	setSearchValue: (event: ChangeEvent<HTMLInputElement>) => void;
	activeSearchType: "product" | "page";
	closeAndClearSearch: () => void;
	resetUiHandler: () => void;
	setActiveProduct: () => void;
	setActivePage: () => void;
	setActiveSearchType: (selectedOption: ActiveSearchType) => void;
	getHelpLinks: Record<
		"faq" | "contactUs" | "shipping",
		{ target: string; url: string }
	>;
};

const QuickSearchComponent: FC<QuickSearchComponentProps> = ({
	activeSearchType,
	isSearchOpen,
	labels,
	pages,
	products,
	searchList,
	searchValue,
	setSearchValue,
	closeAndClearSearch,
	resetUiHandler,
	setActiveProduct,
	setActivePage,
	setActiveSearchType,
	getHelpLinks,
}) => {
	const {
		suggestionsLabel,
		popularLabel,
		productsLabel,
		trendingLabel,
		pagesLabel,
		showAllLabel,
	} = labels;

	const { searchPath } = useTranslatedPaths();

	return (
		<QuickSearch
			closeSearch={closeAndClearSearch}
			resetUiHandler={resetUiHandler}
			activeOption={activeSearchType}
			searchType={searchValue ? "postSearch" : "preSearch"}
			labels={labels}
			isSearchOpen={isSearchOpen}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			setActiveSearchType={setActiveSearchType}
			getHelpLinks={getHelpLinks}
			searchList={
				<List search>
					<h4>{searchValue ? suggestionsLabel : popularLabel}</h4>
					{searchList.map((item, index) => (
						<ListItem
							key={`${item.replace("", "-")}-${index}`}
							variant="list"
							title={item}
							href={`${searchPath}?q=${encodeURIComponent(item)}`}
							searchIcon
						/>
					))}
				</List>
			}
			products={
				<List search hideLabel={Boolean(searchValue)}>
					<h4>{searchValue ? productsLabel : trendingLabel}</h4>
					{products.map((product) => (
						<ListItem
							key={product.uid}
							variant="product"
							title={product.productName}
							image={
								<ImageContainer
									imgSrc={product.productImage.filename}
									alt={product.productImage.alt}
								/>
							}
							price={product.price}
							href={product.productLink}
							description={product.variantLabel}
						/>
					))}
					<ShowAll
						label={showAllLabel}
						onClick={() => {
							setActiveProduct();
						}}
					/>
				</List>
			}
			pages={
				<List search hideLabel={Boolean(searchValue)}>
					<h4>{pagesLabel}</h4>
					{pages.map((page) => (
						<ListItem
							key={page.id}
							variant="pages"
							title={page.name}
							image={
								<ImageContainer
									imgSrc={page.image || "/logo/babybjorn_red_logo.svg"}
									alt={page.alt ?? undefined}
									objectFit={page.image ? "cover" : "contain"}
									ratio={page.image ? "square" : undefined}
								/>
							}
							href={page.url}
							description={page.shortDesc}
						/>
					))}
					<ShowAll
						label={showAllLabel}
						onClick={() => {
							setActivePage();
						}}
					/>
				</List>
			}
		/>
	);
};

export default QuickSearchComponent;
